import { useCallback, useState } from 'react';
import { useLiveSearchParams } from './useLiveSearchParams';

/** URL keys of a keyset list's position: the cursor (before = older page, after = newer page) and the page number. */
export const CURSOR_KEYS = ['before', 'after', 'page'] as const;

const posInt = (s: string | null) => {
  const n = Number(s);
  return Number.isInteger(n) && n > 0 ? n : null;
};

/** The cursor and page step of a keyset list, its keys parsed (ids of the list's own kind: number or ULID). */
type Parse<K> = (s: string | null) => K | null;
const numberId: Parse<number> = posInt;

/**
 * A keyset list's position in the URL: `?before=` / `?after=` (the API's beforeId / afterId), `?page=` (only to say
 * "51–100"; the server says when it is the first page) and `?size=` (one of `sizes`). A page step is a history
 * entry (Back goes to the page before); changing the size, or a filter (`reset`), goes back to the newest page.
 */
export function useCursorParams<K extends number | string = number>(sizes: readonly number[], fallbackSize: number, parse: Parse<K> = numberId as unknown as Parse<K>) {
  const [params, setParams] = useLiveSearchParams();
  const before = parse(params.get('before'));
  const after = parse(params.get('after'));
  const pageNo = posInt(params.get('page')) ?? 1;
  const n = Number(params.get('size'));
  const size = sizes.includes(n) ? n : fallbackSize;

  /** `push`: a page step is a history entry; anything else replaces it. */
  const update = useCallback(
    (changes: Record<string, string | number | null>, opts: { resetCursor?: boolean; push?: boolean } = {}) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (opts.resetCursor) for (const k of CURSOR_KEYS) next.delete(k);
          for (const [k, v] of Object.entries(changes)) {
            if (v === null || v === '') next.delete(k);
            else next.set(k, String(v));
          }
          return next;
        },
        { replace: !opts.push },
      ),
    [setParams],
  );

  const cursor = after !== null ? { afterId: after } : before !== null ? { beforeId: before } : {};
  /** The page shown, from the server's meta: `prev` null means this is the newest page whatever the URL said. */
  const pageIndex = (meta: { prev: K | null } | null | undefined) => (!meta || meta.prev === null ? 1 : Math.max(pageNo, 2));
  const first = () => update({}, { resetCursor: true, push: true });
  const next = (meta: { next: K | null; prev: K | null }) => meta.next !== null && update({ before: meta.next, after: null, page: pageIndex(meta) + 1 }, { push: true });
  const prev = (meta: { next: K | null; prev: K | null }) => {
    if (meta.prev === null) return;
    // back to page 1 is "the newest": no cursor, so new rows show up there
    if (pageIndex(meta) <= 2) first();
    else update({ after: meta.prev, before: null, page: pageIndex(meta) - 1 }, { push: true });
  };
  const setSize = (s: number) => update({ size: s === fallbackSize ? null : s }, { resetCursor: true });
  return { params, update, cursor, size, pageIndex, first, next, prev, setSize };
}

/**
 * The same paging for a keyset list inside a card (a gateway's commands): kept in component state, not the URL,
 * so it never fights the page's own address.
 */
export function useCursorState<K extends number | string = number>() {
  const [pos, setPos] = useState<{ cursor: { beforeId?: K; afterId?: K }; page: number }>({ cursor: {}, page: 1 });
  const pageIndex = (meta: { prev: K | null } | null | undefined) => (!meta || meta.prev === null ? 1 : Math.max(pos.page, 2));
  const first = () => setPos({ cursor: {}, page: 1 });
  const next = (meta: { next: K | null; prev: K | null }) => meta.next !== null && setPos({ cursor: { beforeId: meta.next }, page: pageIndex(meta) + 1 });
  const prev = (meta: { next: K | null; prev: K | null }) => {
    if (meta.prev === null) return;
    if (pageIndex(meta) <= 2) first();
    else setPos({ cursor: { afterId: meta.prev }, page: pageIndex(meta) - 1 });
  };
  return { cursor: pos.cursor, pageIndex, first, next, prev };
}
